import React, { useState, useEffect, useContext } from "react";
import { MessageSidebar } from "./MessageSidebar";
import { ChatArea } from "./ChatArea";
import io from "socket.io-client";
import useAxiosInstance from "../../../lib/useAxiosInstance";

export const MessagesTab = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const axiosInstance = useAxiosInstance();

    const [selectedChatId, setSelectedChatId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [chats, setChats] = useState([]);
    const [messages, setMessages] = useState({});
    const [socket, setSocket] = useState(null);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [nextCursor, setNextCursor] = useState(null);


    // 🔗 Connect socket
    useEffect(() => {
        if (!user?._id) return;

        const s = io(process.env.REACT_APP_API_URL, {
        query: { userId: user._id },
        transports: ["websocket", "polling", "flashsocket"],
        });

        setSocket(s);

        // Listen for new messages
        s.on("message:new", (msg) => {
        setMessages((prev) => ({
            ...prev,
            [msg.conversation]: [msg, ...(prev[msg.conversation] || [])],
        }));
        // Update lastMessage in chat list
        setChats((prev) =>
            prev.map((chat) =>
            chat._id === msg.conversation
                ? { ...chat, lastMessage: msg?.text, lastMessageTime: msg?.createdAt }
                : chat
            )
        );
        });

        // Listen for new conversations
        s.on("conversation:new", (convo) => {
        setChats((prev) => {
            const exists = prev.find((c) => c._id === convo._id);
            if (exists) return prev;
            return [...prev, convo];
        });
        });

        return () => {
        s.disconnect();
        };
    }, [user?._id]);
    useEffect(() => {
        if (!socket || !selectedChatId) return;
        // Join the room whenever a chat is selected
        socket.emit("joinRoom", selectedChatId);
    }, [socket, selectedChatId]);
    useEffect(() => {
        const fetchConversations = async () => {
        try {
            const res = await axiosInstance.get("api/chat/conversations");
            if (res.data?.status) {
            setChats(res?.data?.data || []);
            }
        } catch (err) {
            console.error("Error fetching conversations:", err);
        }
        };

        fetchConversations();
    }, []);

    // Fetch messages when chat changes
    useEffect(() => {
        if (!socket || !selectedChatId) return;

        const fetchMessages = async () => {
        try {
            setLoadingMessages(true);
            const res = await axiosInstance.get(
            `api/chat/conversations/${selectedChatId}/messages?limit=20`
            );

            if (res.data?.status) {
            setMessages((prev) => ({
                ...prev,
                [selectedChatId]: res.data.data.items, // oldest → newest
            }));
            setNextCursor(res.data.data.nextCursor);
            }
        } catch (err) {
            console.error("Error fetching messages:", err);
        } finally {
            setLoadingMessages(false);
        }
        };

        // Join the socket room
        socket.emit("joinRoom", selectedChatId);
        // Fetch from API
        fetchMessages();
    }, [selectedChatId])

    useEffect(() => {
    // make this accessible globally
    window.openChat = (chatId) => {
    setSelectedChatId(chatId);
    };
    }, []);


    const loadMoreMessages = async () => {
        if (!nextCursor || !selectedChatId) return;
        setLoadingMessages(true);
        try {
        const res = await axiosInstance.get(
            `api/chat/conversations/${selectedChatId}/messages?limit=20&before=${nextCursor}`
        );

        if (res.data?.status) {
            setMessages((prev) => ({
            ...prev,
            [selectedChatId]: [
                ...(prev[selectedChatId] || []),
                ...res.data.data.items.reverse(),
            ],
            }));
            setNextCursor(res.data.data.nextCursor);
        }
        } catch (err) {
        console.error("Error loading more messages:", err);
        } finally {
            setLoadingMessages(false);
        }
    };

    // Add contact = create conversation
    const handleAddContact = async (contactUser) => {
        try {
        const res = await axiosInstance.post("api/chat/conversations", {
        participants: [
            { user: contactUser._id, role: "member" }
        ],
        isGroup:false
        });
        const convo = res?.data?.data;
        setChats((prev) => [...prev, convo]);
        setSelectedChatId(convo._id);

        socket?.emit("joinRoom", convo._id);
        } catch (err) {
        console.error("Error creating conversation:", err);
        }
    };

    const handleSendMessage = async (content, attachments = []) => {
        if (!selectedChatId || !socket) return;

        let uploadedAttachments = [];

        if (attachments.length > 0) {
            // Prepare form data
            const formData = new FormData();
            attachments.forEach((file) => formData.append("chatImage", file));
            // Upload files to backend S3 endpoint
            try {
            const res = await axiosInstance.post(
                `api/chat/attachments/upload`,
                formData
                // { headers: { "Content-Type": "multipart/form-data" } }
            );
            uploadedAttachments = res.data.data; // backend should return array of { url, name, type }
            } catch (err) {
            console.error("File upload failed", err);
            return;
            }
        }

        // Emit message with attachments
        socket.emit("sendMessage", {
            conversationId: selectedChatId,
            senderId: user._id,
            text: content,
            attachments: uploadedAttachments,
        });
    };

    const filteredChats = chats.filter(
        (chat) =>
        chat?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        // chat?.lastMessage?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedChat = chats.find((chat) => chat._id === selectedChatId);

    return (
        <div className="directory-wrapper rounded-lg shadow-sm h-[calc(100vh-170px)] flex">
        <MessageSidebar
            chats={chats}
            selectedChatId={selectedChatId}
            onChatSelect={setSelectedChatId}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onAddContact={handleAddContact}
        />

        <ChatArea
            chat={selectedChat}
            messages={messages[selectedChatId] || []}
            onSendMessage={handleSendMessage}
            onLoadMore={loadMoreMessages}
            loading={loadingMessages}
        />
        </div>
    );
};
